import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import Swal from "sweetalert2";
import PharmacySoftwareService from "../../services/pharmacy-software.service";
import "./index.scss";

const SEND_MODES = [
  {
    id: "on_hold",
    label: "Add On Hold",
    hint: "Creates the prescription on hold with the prescriber, directions and quantity.",
  },
  {
    id: "intake",
    label: "Intake",
    hint: "Queues the prescription image for intake with minimal data.",
  },
];

const STEP_ICONS = {
  ok: "fa-solid fa-circle-check text-success",
  failed: "fa-solid fa-circle-xmark text-danger",
  skipped: "fa-solid fa-circle-minus text-muted",
};

const formatDateTime = (value) =>
  value ? moment(value).format("MMM DD, YYYY hh:mm A") : "-";

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

const resolutionLabel = (value) => {
  if (value === "created") return "created in Pioneer";
  if (value === "matched") return "matched existing";
  if (value === "skipped") return "not needed";
  return null;
};

const formatJson = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const isPioneerHit = (entry) =>
  Boolean(entry?.detail && (entry.detail.endpoint || entry.detail.request || entry.detail.response));

const isLiveScriptImage = (value) => {
  if (typeof value !== "string" || value.length < 80) return false;
  const trimmed = value.replace(/^data:application\/pdf;base64,/i, "");
  return trimmed.startsWith("JVBERi") || (!trimmed.startsWith("<") && /^[A-Za-z0-9+/=\s]+$/.test(trimmed));
};

const readStoredScriptImage = (source) => {
  if (!source) return null;
  const steps = source.Steps || source.steps || [];
  const candidates = [
    source.RequestPayload?.scriptImage,
    source.requestPayload?.scriptImage,
    source.detail?.request?.scriptImage,
    source.detail?.scriptImage,
    ...steps.map((step) => step?.detail?.request?.scriptImage),
    ...steps.map((step) => step?.detail?.scriptImage),
  ];
  const found = candidates.find(isLiveScriptImage);
  return found ? found.replace(/^data:application\/pdf;base64,/i, "") : null;
};

const pdfUrlFromBase64 = (base64) => {
  const clean = String(base64 || "").replace(/^data:application\/pdf;base64,/i, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return window.URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const readNestedPioneerRxNumber = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const results =
    payload.pioneer?.results ||
    payload.results ||
    payload.pioneer ||
    payload;
  const tx = results?.rxTransaction || results?.RxTransaction;
  const first = Array.isArray(tx) ? tx[0] : tx;
  const value = first?.rxNumber ?? first?.RxNumber;
  return value != null && String(value).trim() !== "" ? String(value).trim() : null;
};

const readPioneerRxNumber = (source) => {
  if (!source) return null;
  const nested =
    readNestedPioneerRxNumber(source.ResponsePayload) ||
    readNestedPioneerRxNumber(source.detail?.response) ||
    readNestedPioneerRxNumber(source);
  if (nested) return nested;
  const fromSteps = (source.steps || source.Steps || [])
    .map((step) => readNestedPioneerRxNumber(step?.detail?.response))
    .find(Boolean);
  if (fromSteps) return fromSteps;
  const stored = source.PioneerRxId || source.pioneerRxId;
  if (stored && !GUID_RE.test(String(stored))) return String(stored);
  return stored ? String(stored) : null;
};

const PioneerHitLogs = ({ entry }) => {
  const [copied, setCopied] = useState("");
  const detail = entry?.detail || {};
  const request = detail.request;
  const response = detail.response;
  const error = detail.error ?? entry?.summary;
  const endpoint = detail.endpoint;
  const httpStatus = detail.httpStatus;
  const method = detail.method || "POST";

  const copy = async (key, value) => {
    const text = formatJson(value);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      setCopied("");
    }
  };

  const Block = ({ title, value, copyKey }) => {
    const text = formatJson(value);
    if (!text) return null;
    return (
      <div className="software-tab__hit-block">
        <div className="software-tab__hit-block-head">
          <strong>{title}</strong>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => copy(copyKey, value)}
          >
            {copied === copyKey ? "Copied" : "Copy"}
          </button>
        </div>
        <pre>{text}</pre>
      </div>
    );
  };

  return (
    <div className="software-tab__hit">
      <div className="software-tab__hit-meta">
        {endpoint ? (
          <span>
            <span className="software-tab__label">Endpoint</span>
            <code>
              {method} {endpoint}
            </code>
          </span>
        ) : null}
        {httpStatus ? (
          <span>
            <span className="software-tab__label">HTTP</span>
            <strong>{httpStatus}</strong>
          </span>
        ) : null}
        {error ? (
          <span>
            <span className="software-tab__label">Error</span>
            <strong className="text-danger">{error}</strong>
          </span>
        ) : null}
      </div>
      <Block title="Request payload" value={request} copyKey="request" />
      <Block title="Response" value={response} copyKey="response" />
      {!request && !response ? (
        <p className="text-muted small mb-0">
          No Pioneer request or response was stored for this step.
        </p>
      ) : null}
    </div>
  );
};

const SoftwareTab = ({ requestId }) => {
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendMode, setSendMode] = useState("on_hold");
  const [lastResult, setLastResult] = useState(null);
  const [expandedLogIds, setExpandedLogIds] = useState([]);
  const [expandedHitKeys, setExpandedHitKeys] = useState([]);
  const [isOpeningScript, setIsOpeningScript] = useState(false);
  const [scriptImageUrl, setScriptImageUrl] = useState(null);

  const loadState = useCallback(async () => {
    if (!requestId) return;
    setIsLoading(true);
    const response = await PharmacySoftwareService.GetForRequest(requestId);
    setIsLoading(false);
    if (response?.error) return;

    const data = response?.data || null;
    setState(data);
    if (data?.connection?.DefaultSendMode) {
      setSendMode(data.connection.DefaultSendMode);
    }
  }, [requestId]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const connection = state?.connection || null;
  const readiness = state?.readiness || { issues: [], canSendOnHold: false, canSendIntake: false };
  const summary = state?.summary || {};
  const history = state?.history || [];

  const successfulLog = history.find((entry) => entry.Status === "success") || null;
  const completedResult =
    lastResult?.status === "success"
      ? lastResult
      : successfulLog
      ? {
          status: "success",
          sendMode: successfulLog.SendMode,
          sendModeLabel: successfulLog.SendModeLabel,
          pioneerRxId: readPioneerRxNumber(successfulLog),
          steps: successfulLog.Steps || [],
        }
      : null;
  const isDone = Boolean(completedResult);
  const pioneerRxNumber =
    readPioneerRxNumber(completedResult) || readPioneerRxNumber(successfulLog);
  const resultToShow =
    lastResult?.status === "failed" && !isDone ? lastResult : completedResult || lastResult;

  const canSendSelectedMode =
    sendMode === "on_hold" ? readiness.canSendOnHold : readiness.canSendIntake;

  const blockingIssues = readiness.issues.filter(
    (issue) =>
      issue.severity === "blocking" &&
      (issue.requiredFor === "both" || issue.requiredFor === sendMode)
  );
  const warnings = readiness.issues.filter((issue) => issue.severity === "warning");

  const sendHandler = async () => {
    const confirmation = await Swal.fire({
      icon: "question",
      title: `Send to ${connection.SoftwareLabel}?`,
      html: `This creates the prescription in <strong>${connection.PharmacyBranchName}</strong> using <strong>${
        SEND_MODES.find((mode) => mode.id === sendMode)?.label
      }</strong>.`,
      showCancelButton: true,
      confirmButtonText: "Send",
    });
    if (!confirmation.isConfirmed) return;

    setIsSending(true);
    setLastResult(null);
    const response = await PharmacySoftwareService.Send(requestId, sendMode);
    setIsSending(false);

    // The API answers 200 with status "failed" when the pharmacy software rejects the
    // prescription, so the outcome lives in the payload rather than in response.error.
    if (response?.error || !response?.data) {
      Swal.fire({
        icon: "error",
        title: "Could not send",
        text: response?.message || "Please try again.",
      });
      await loadState();
      return;
    }

    setLastResult(response.data);
    if (response.data?.status !== "success") {
      const failedIndex = (response.data.steps || []).findIndex(
        (entry) => entry.status === "failed" && isPioneerHit(entry)
      );
      if (failedIndex >= 0) {
        setExpandedHitKeys([`result-${failedIndex}`]);
      }
    }
    await loadState();
  };

  const toggleLog = (id) => {
    setExpandedLogIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleHit = (key) => {
    setExpandedHitKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  };

  const closeScriptImage = () => {
    if (scriptImageUrl) {
      window.URL.revokeObjectURL(scriptImageUrl);
    }
    setScriptImageUrl(null);
  };

  const showScriptImage = (base64) => {
    if (scriptImageUrl) {
      window.URL.revokeObjectURL(scriptImageUrl);
    }
    setScriptImageUrl(pdfUrlFromBase64(base64));
  };

  const openScriptImage = async (entry) => {
    if (isOpeningScript) return;

    const stored =
      readStoredScriptImage(entry) ||
      readStoredScriptImage(completedResult) ||
      readStoredScriptImage(successfulLog);
    if (stored) {
      showScriptImage(stored);
      return;
    }

    setIsOpeningScript(true);
    const result = await PharmacySoftwareService.GetScriptImage(requestId);
    setIsOpeningScript(false);

    const payload = result?.data?.base64
      ? result.data
      : result?.data?.data || result?.responseData?.data?.data || result?.responseData?.data;
    const base64 = payload?.base64;
    if (result?.error || !base64) {
      Swal.fire({
        icon: "error",
        title: "Could not open script image",
        text:
          (typeof result?.message === "string" && result.message) ||
          "The prescription sheet could not be loaded. Try again after the backend update is live.",
      });
      return;
    }
    showScriptImage(base64);
  };

  const renderSteps = (steps, keyPrefix) => (
    <ol className="software-tab__steps">
      {steps.map((entry, index) => {
        const hitKey = `${keyPrefix}-${index}`;
        const showLogs = isPioneerHit(entry);
        return (
          <li key={`${entry.step}-${index}`}>
            <i className={STEP_ICONS[entry.status] || STEP_ICONS.skipped}></i>
            <div>
              <strong>{entry.label || entry.step}</strong>
              {entry.summary ? <div className="text-muted small">{entry.summary}</div> : null}
              {showLogs && expandedHitKeys.includes(hitKey) ? (
                <PioneerHitLogs entry={entry} />
              ) : null}
            </div>
            <div className="software-tab__step-actions">
              {showLogs ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => toggleHit(hitKey)}
                >
                  {expandedHitKeys.includes(hitKey) ? "Hide logs" : "Logs"}
                </button>
              ) : null}
              {formatDuration(entry.durationMs) ? (
                <span className="software-tab__step-duration">
                  {formatDuration(entry.durationMs)}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );

  if (isLoading && !state) {
    return <p className="text-muted mb-0">Loading connected software...</p>;
  }

  if (!connection) {
    return (
      <div className="software-tab">
        <div className="alert alert-secondary mb-0">
          <h6 className="mb-1">No pharmacy software connected</h6>
          <p className="mb-0">
            This pharmacy is not connected to an external pharmacy system yet, so prescriptions
            cannot be pushed from here. A TransferMyRx administrator can add the connection under
            Pharmacy Software.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="software-tab">
      {isDone && pioneerRxNumber ? (
        <div className="software-tab__pioneer-rx">
          <span className="software-tab__label">Pioneer Rx number</span>
          <div className="software-tab__pioneer-rx-value">
            <strong>{pioneerRxNumber}</strong>
            <span className="badge bg-success">
              {completedResult.sendMode === "intake" || completedResult.sendModeLabel === "Intake"
                ? "Intake"
                : "On Hold"}{" "}
              · Done
            </span>
          </div>
        </div>
      ) : null}

      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Connected Software</span>
          <span className={`badge bg-${connection.IsActive ? "success" : "secondary"}`}>
            {connection.IsActive ? "Active" : "Disabled"}
          </span>
        </div>
        <div className="card-body">
          <div className="row software-tab__facts">
            <div className="col-md-4">
              <span className="software-tab__label">Software</span>
              <strong>{connection.SoftwareLabel}</strong>
            </div>
            <div className="col-md-4">
              <span className="software-tab__label">Pharmacy</span>
              <strong>{connection.PharmacyBranchName || "-"}</strong>
            </div>
            <div className="col-md-4">
              <span className="software-tab__label">Host</span>
              <code>{connection.Host}</code>
            </div>
          </div>
          {connection.LastTestStatus ? (
            <p className="text-muted small mb-0 mt-2">
              Last connection test {connection.LastTestStatus === "success" ? "passed" : "failed"}{" "}
              on {formatDateTime(connection.LastTestedOn)}.
            </p>
          ) : null}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>{isDone ? "Sent to PioneerRx" : "Send this Rx"}</span>
          {isDone ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => openScriptImage(successfulLog)}
              disabled={isOpeningScript}
            >
              {isOpeningScript ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin me-1"></i>
                  Opening…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-file-pdf me-1"></i>
                  Script image
                </>
              )}
            </button>
          ) : null}
        </div>
        <div className="card-body">
          <div className="row software-tab__facts mb-3">
            {isDone && pioneerRxNumber ? (
              <div className="col-md-3">
                <span className="software-tab__label">Pioneer Rx number</span>
                <strong>{pioneerRxNumber}</strong>
              </div>
            ) : null}
            <div className="col-md-3">
              <span className="software-tab__label">Rx number</span>
              <strong>{summary.rxNumber || "-"}</strong>
            </div>
            <div className="col-md-3">
              <span className="software-tab__label">Patient</span>
              <strong>{summary.patientName || "-"}</strong>
            </div>
            <div className="col-md-3">
              <span className="software-tab__label">Drug</span>
              <strong>{summary.drugName || "-"}</strong>
            </div>
            <div className="col-md-3">
              <span className="software-tab__label">Prescriber</span>
              <strong>{summary.prescriberName || "-"}</strong>
            </div>
          </div>

          {isDone ? null : (
            <>
              <div className="software-tab__modes">
                {SEND_MODES.map((mode) => {
                  const allowed =
                    mode.id === "on_hold" ? readiness.canSendOnHold : readiness.canSendIntake;
                  return (
                    <label
                      key={mode.id}
                      className={`software-tab__mode ${sendMode === mode.id ? "is-selected" : ""} ${
                        allowed ? "" : "is-blocked"
                      }`}
                    >
                      <input
                        type="radio"
                        name="sendMode"
                        value={mode.id}
                        checked={sendMode === mode.id}
                        onChange={() => setSendMode(mode.id)}
                      />
                      <span>
                        <strong>{mode.label}</strong>
                        {connection.DefaultSendMode === mode.id ? (
                          <span className="badge bg-light text-dark ms-2">Default</span>
                        ) : null}
                        <span className="d-block text-muted small">{mode.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {blockingIssues.length > 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  <strong>This Rx cannot be sent yet.</strong> Add the following first:
                  <ul className="mb-0 mt-1">
                    {blockingIssues.map((issue) => (
                      <li key={issue.field}>{issue.label}</li>
                    ))}
                  </ul>
                </div>
              )}

              {blockingIssues.length === 0 && warnings.length > 0 && (
                <div className="alert alert-light border mt-3 mb-0">
                  <strong>Heads up.</strong>
                  <ul className="mb-0 mt-1">
                    {warnings.map((issue) => (
                      <li key={issue.field}>{issue.label}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={sendHandler}
                  disabled={isSending || !connection.IsActive || !canSendSelectedMode}
                >
                  {isSending ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin me-2"></i>
                      Sending to {connection.SoftwareLabel}...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane me-2"></i>
                      Send to {connection.SoftwareLabel}
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {resultToShow ? (
            <div
              className={`alert mt-3 mb-0 alert-${
                resultToShow.status === "success" ? "success" : "danger"
              }`}
            >
              <h6 className="mb-1">
                {resultToShow.status === "success"
                  ? `${resultToShow.sendModeLabel} accepted`
                  : `${resultToShow.sendModeLabel} failed`}
              </h6>
              {resultToShow.status === "success" ? (
                <p className="mb-2">
                  {pioneerRxNumber
                    ? `Created as Rx ${pioneerRxNumber}.`
                    : "The pharmacy software accepted the prescription."}
                </p>
              ) : (
                <p className="mb-2">{resultToShow.error}</p>
              )}
              {resultToShow.steps?.length
                ? renderSteps(resultToShow.steps, resultToShow.status === "success" ? "done" : "result")
                : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-header">Software History</div>
        <div className="card-body">
          {history.length === 0 ? (
            <p className="text-muted mb-0">
              This Rx has not been sent to any pharmacy software yet.
            </p>
          ) : (
            history.map((entry) => (
              <div className="software-tab__entry" key={entry.Id}>
                <div className="software-tab__entry-head">
                  <div>
                    <span
                      className={`badge bg-${entry.Status === "success" ? "success" : "danger"} me-2`}
                    >
                      {entry.Status === "success" ? "Sent" : "Failed"}
                    </span>
                    <strong>
                      {entry.SoftwareLabel} · {entry.SendModeLabel}
                    </strong>
                    <span className="text-muted small ms-2">
                      {formatDateTime(entry.SentOn)} by {entry.SentByName || "Unknown user"}
                    </span>
                  </div>
                  <div className="software-tab__entry-actions">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => openScriptImage(entry)}
                      disabled={isOpeningScript}
                    >
                      {isOpeningScript ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin me-1"></i>
                          Opening…
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-file-pdf me-1"></i>
                          Script image
                        </>
                      )}
                    </button>
                    {entry.RequestPayload ||
                    entry.ResponsePayload ||
                    (entry.Steps || []).some(isPioneerHit) ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => toggleHit(`entry-${entry.Id}`)}
                      >
                        {expandedHitKeys.includes(`entry-${entry.Id}`) ? "Hide logs" : "Logs"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => toggleLog(entry.Id)}
                    >
                      {expandedLogIds.includes(entry.Id) ? "Hide details" : "Details"}
                    </button>
                  </div>
                </div>

                {entry.Status === "success" && readPioneerRxNumber(entry) ? (
                  <p className="mb-1 small">
                    Created as Rx <code>{readPioneerRxNumber(entry)}</code>
                  </p>
                ) : null}
                {entry.Status === "failed" && entry.ErrorMessage ? (
                  <p className="mb-1 small text-danger">{entry.ErrorMessage}</p>
                ) : null}

                <div className="software-tab__entry-meta">
                  {entry.ResolvedPatientId ? (
                    <span>
                      Patient {entry.ResolvedPatientId}
                      {resolutionLabel(entry.PatientResolution)
                        ? ` (${resolutionLabel(entry.PatientResolution)})`
                        : ""}
                    </span>
                  ) : null}
                  {entry.ResolvedPrescriberId ? (
                    <span>
                      Prescriber {entry.ResolvedPrescriberId}
                      {resolutionLabel(entry.PrescriberResolution)
                        ? ` (${resolutionLabel(entry.PrescriberResolution)})`
                        : ""}
                    </span>
                  ) : null}
                  {entry.MedicationNdc ? <span>NDC {entry.MedicationNdc}</span> : null}
                  {formatDuration(entry.DurationMs) ? (
                    <span>Took {formatDuration(entry.DurationMs)}</span>
                  ) : null}
                </div>

                {expandedHitKeys.includes(`entry-${entry.Id}`) ? (
                  <div className="software-tab__entry-logs">
                    {(entry.Steps || []).some(isPioneerHit) ? (
                      (entry.Steps || []).filter(isPioneerHit).map((step, index) => (
                        <div key={`${step.step}-${index}`}>
                          <div className="software-tab__hit-title">
                            <i className={STEP_ICONS[step.status] || STEP_ICONS.skipped}></i>
                            {step.label || step.step}
                          </div>
                          <PioneerHitLogs entry={step} />
                        </div>
                      ))
                    ) : (
                      <PioneerHitLogs
                        entry={{
                          summary: entry.ErrorMessage,
                          detail: {
                            error: entry.ErrorMessage,
                            request: entry.RequestPayload,
                            response: entry.ResponsePayload,
                          },
                        }}
                      />
                    )}
                  </div>
                ) : null}

                {expandedLogIds.includes(entry.Id) && entry.Steps?.length
                  ? renderSteps(entry.Steps, `history-${entry.Id}`)
                  : null}
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        show={Boolean(scriptImageUrl)}
        onHide={closeScriptImage}
        size="xl"
        centered
        scrollable
        className="software-tab__script-modal"
        backdropClassName="software-tab-script-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Script image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {scriptImageUrl ? (
            <iframe
              title="Pioneer script image"
              src={scriptImageUrl}
              className="software-tab__script-frame"
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SoftwareTab;
